import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import { Store } from 'src/store';
import getForwardUrl from 'src/store/app/selector/getForwardUrl';
import goToOptimalprintOperation from 'src/store/app/operation/goToOptimalprintOperation';
import hidePopupOperation from 'src/store/app/operation/hidePopupOperation';
import showPopupOperation from 'src/store/app/operation/showPopupOperation';
import { showPopupAction } from 'src/store/app/slice';
import sendEmptyProductWarningOperation from 'src/store/app/operation/sendEmptyProductWarningOperation';
import { Feature, EDITOR_CROSSSELL_POPUP_ID } from 'src/store/app/types';
import updateCustomerDesignOperation from 'src/store/design/operation/updateCustomerDesignOperation';
import getAbstractProduct from 'src/store/design/selector/getAbstractProduct';
import getDesignId from 'src/store/design/selector/getDesignId';
import getSelectedProductAttributes from 'src/store/design/selector/getSelectedProductAttributes';
import getProductUid from 'src/store/design/selector/getProductUid';
import getStructure from 'src/store/design/selector/getStructure';
import isDesignSaving from 'src/store/design/selector/isDesignSaving';
import shouldDisplayLowDPIWarning from 'src/store/design/selector/shouldDisplayLowDPIWarning';
import { setDesignIsLoadingAction, setDesignIsSavingAction, setDesignOrderItemIdAction } from 'src/store/design/slice';
import trackEventOperation from 'src/store/addon/operation/trackEventOperation';
import { clearMultiSizePendingItemsAction } from 'src/store/multisize/slice';
import {
  findColorSelector, findSizeSelector, getVariantTypeNames, resolveSizeVariant,
} from 'src/util/apparel/sizeVariants';
import isDesignHasEmptyContent from 'src/util/design/isDesignHasEmptyContent';
import resolveBoundProduct, { getVariantAttributes } from 'src/util/design/resolveBoundProduct';
import isFeatureEnabled from 'src/util/feature/isFeatureEnabled';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';

/**
 * OP-39782: commit a multi-size apparel selection as one grouped basket line.
 *
 * Mirrors the single-add pre-commit chain (empty / low-DPI guards + design save)
 * then commits via the personalised batch endpoint (OP-39659), which clones the
 * base design per size and adds each as an order item, and redirects to basket.
 *
 * v1 scope: no bundled products, no DPI/hanger/remove-logo add-ons and no
 * pre-basket upsell — the batch endpoint carries only {productId, quantity}.
 */
const multiSizeAddToBasketOperation = () => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
  const store = getState();
  const { pendingItems } = store.multisize;
  const designData = getStructure(store);

  if (isDesignSaving(store) || pendingItems.length === 0) {
    return;
  }

  if (isFeatureEnabled(Feature.BlockPopupWhenEmptyDesign) && designData && isDesignHasEmptyContent(designData)) {
    dispatch(sendEmptyProductWarningOperation());
    return;
  }

  dispatch(hidePopupOperation());

  if (isDesignHasEmptyContent(designData)) {
    dispatch(trackEventOperation('customerAttemptAddToCartEmptyDesign'));
  }

  if (shouldDisplayLowDPIWarning(store)) {
    dispatch(showPopupOperation('LowDPIWarningPopup'));
    return;
  }

  try {
    dispatch(setDesignIsLoadingAction(true));
    dispatch(setDesignIsSavingAction(true));

    const designUpdateResult = await dispatch(updateCustomerDesignOperation());
    if (!designUpdateResult) {
      dispatch(showPopupOperation('UpdateDesignWarningPopup'));
      dispatch(trackEventOperation('customerDesignUpdateFailed'));
      throw new Error('Error updating customer design');
    }

    // Resolve each pending size to a concrete product only now, after the save,
    // and off the bound variant rather than the product-page selection: the batch
    // endpoint carries explicit productIds, so resolving them any other way adds
    // basket lines for a variant the design was not saved as, which blocks
    // production (see resolveBoundProduct).
    const savedStore = getState();
    const abstractProduct = getAbstractProduct(savedStore);
    const boundProduct = resolveBoundProduct(abstractProduct, getStructure(savedStore));

    // No bound product means the uid the design was saved as isn't in the catalogue.
    // Falling back to the product-page attributes here is what shipped a variant the
    // design was never saved as, so refuse rather than guess.
    if (!boundProduct) {
      dispatch(showPopupOperation('UpdateDesignWarningPopup'));
      throw new Error('Cannot resolve the product the design is bound to');
    }

    const sizeTypeName = findSizeSelector(abstractProduct?.editorSelectors)?.typeName || '';
    const colorTypeName = findColorSelector(abstractProduct?.editorSelectors)?.typeName || '';
    const boundAttributes = getVariantAttributes(boundProduct, getSelectedProductAttributes(savedStore) || {});
    const variantTypeNames = getVariantTypeNames(abstractProduct);

    const items = pendingItems
      .map(({ sizeValue, colorValue, quantity }) => {
        // Mixed-colour items (OP-40008) override the bound variant's colour
        // dimension; size-only items keep the bound colour untouched.
        const attributes = colorValue && colorTypeName
          ? { ...boundAttributes, [colorTypeName]: { typeName: colorTypeName, value: colorValue } }
          : boundAttributes;
        const product = resolveSizeVariant(abstractProduct?.products || [], variantTypeNames, sizeTypeName, sizeValue, attributes);
        return product ? { productId: product.productId, quantity } : undefined;
      })
      .filter((item): item is { productId: number; quantity: number } => !!item);

    // A size with no sibling for the bound variant cannot be produced as designed.
    // Commit all of them or none: a partly-filled basket line is silent data loss,
    // and committing a substitute variant is what blocks production in the first place.
    if (items.length !== pendingItems.length) {
      dispatch(showPopupOperation('UpdateDesignWarningPopup'));
      throw new Error(`${pendingItems.length - items.length} multi-size item(s) have no variant for the bound product`);
    }

    const batchResult = (await opApiRequest(
      endpoints.CART_V1_DESIGN_BATCH_ADD,
      { customerDesignId: getDesignId(store), items },
      'data',
      'POST',
    )) as { items?: Array<{ orderItemId?: number }> };

    // Keep one of the newly-created order items so the cross-sell popup can scope
    // its personalised previews to the design just added (the batch clones the
    // design per size, so the original design id no longer maps to an order item).
    const addedOrderItemId = batchResult?.items?.[0]?.orderItemId;
    if (addedOrderItemId) {
      dispatch(setDesignOrderItemIdAction(addedOrderItemId));
    }

    dispatch(clearMultiSizePendingItemsAction());

    const { categoryName, isApparel } = useIntegrationLayer();
    window.dataLayer.push({
      event: 'editorAddToBasket',
      category: categoryName,
      product: getProductUid(store),
      editorVersion: 'New',
      addMode: 'multisize',
    });

    // Post-add cross-sell: apparel-only, terminal surface before the cart.
    if (isApparel && isFeatureEnabled(Feature.editorCrossSell) && getDesignId(getState())) {
      // Open without a backdrop: the popup mounts hidden and prefetches, then
      // reveals itself (with the backdrop) only if it has items to show.
      dispatch(showPopupAction(EDITOR_CROSSSELL_POPUP_ID));
      dispatch(setDesignIsLoadingAction(false));
      dispatch(setDesignIsSavingAction(false));
      return;
    }

    dispatch(goToOptimalprintOperation(getForwardUrl(store)));
  } catch (e) {
    dispatch(setDesignIsLoadingAction(false));
    dispatch(setDesignIsSavingAction(false));
    // eslint-disable-next-line no-console
    console.error('Cannot add multi-size design to basket', e);
  }
};

export default multiSizeAddToBasketOperation;
